// 통합 현황 — ops-britzmedi margin/Consolidated.tsx 를 contracts+contract_sales
// 집계 기반으로 개작 (FR-11). 전 계약 달성 현황 + 제품별/국가별 실적 요약.
import { useMemo, useState } from "react";
import type { ContractRow, ContractSale, Product } from "@/types";
import { contractProgress, krw, pct, usd } from "@/lib/margin/model";
import { contractRowToModel, saleToModel } from "./convert";
import { aggregateSales, type Agg } from "./aggregate";
import { neg, TD, TH } from "./ui";
import { QuotaTrack } from "./ContractCard";

export default function ConsolidatedView({
  contracts,
  sales,
  products,
}: {
  contracts: ContractRow[];
  sales: ContractSale[];
  products: Product[];
}): JSX.Element {
  const years = useMemo(() => {
    const ys = new Set(sales.map((s) => s.sale_date.slice(0, 4)).filter(Boolean));
    ys.add(String(new Date().getFullYear()));
    return [...ys].sort().reverse();
  }, [sales]);
  const [year, setYear] = useState<string>("all");

  const productName = (ct: ContractRow) =>
    products.find((p) => p.id === ct.product_id)?.model_name ?? ct.sim_product ?? "미지정";

  const { byProduct, byCountry, total, count } = useMemo(
    () => aggregateSales(sales, contracts, products, year),
    [sales, contracts, products, year],
  );

  const HEAD = ["수량", "매출 USD", "매출 KRW", "총비용 KRW", "마진 KRW", "마진율"];
  const cells = (a: Agg, bold = false) => (
    <>
      <td className={TD + (bold ? " font-bold" : "")}>{usd(a.qty)}</td>
      <td className={TD + (bold ? " font-bold" : "")}>{usd(a.revUsd)}</td>
      <td className={TD + (bold ? " font-bold" : "")}>{krw(a.revKrw)}</td>
      <td className={TD + (bold ? " font-bold" : "")}>{krw(a.cost)}</td>
      <td className={TD + " font-bold" + neg(a.margin)}>{krw(a.margin)}</td>
      <td className={TD + (bold ? " font-bold" : "")}>{pct(a.revKrw > 0 ? a.margin / a.revKrw : NaN)}</td>
    </>
  );

  const sectionTitle = (t: string, hint?: string) => (
    <h4 className="mb-2 flex items-center gap-2 text-xs font-bold text-graphite before:h-3 before:w-[3px] before:rounded-sm before:bg-brass before:content-['']">
      {t}
      {hint && <span className="font-normal text-graphite-mut">{hint}</span>}
    </h4>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-base font-semibold tracking-tight text-graphite">BRITZMEDI 해외 수출 통합 현황</h3>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-graphite focus:border-brass focus:outline-none"
        >
          <option value="all">전체 연도</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}년
            </option>
          ))}
        </select>
      </div>

      {/* 계약 조건·달성 현황 */}
      <section>
        {sectionTitle("계약 조건 · 쿼터 달성 현황", '(시뮬레이터에서 "계약 등록"한 확정 조건)')}
        {contracts.length === 0 ? (
          <p className="rounded-md border border-dashed border-line bg-surface px-3 py-3 text-xs text-graphite-mut">
            등록된 계약이 없습니다. 시뮬레이션 케이스에서 가격안 카드의 "이 안으로 계약 등록"을 누르면 여기에 취합됩니다.
          </p>
        ) : (
          <div className="space-y-3">
            {contracts.map((row) => {
              const ct = contractRowToModel(row);
              const ctSales = sales.filter((s) => s.contract_id === row.id).map(saleToModel);
              const pr = contractProgress(ct, ctSales);
              return (
                <div key={row.id} className="rounded-lg border border-line bg-surface p-3">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-sm">
                    <span className="rounded bg-ink px-1.5 py-0.5 text-[10px] font-semibold text-white">{productName(row)}</span>
                    <b className="text-graphite">{row.name}</b>
                    {row.buyer_name && <span className="text-graphite-mut">{row.buyer_name}</span>}
                    {row.status !== "active" && (
                      <span className="rounded border border-line bg-surface-2 px-1.5 py-0.5 text-[10px] text-graphite-mut">
                        {row.status === "done" ? "종료" : "취소"}
                      </span>
                    )}
                    {(row.start_date || row.end_date) && (
                      <span className="font-mono text-xs text-graphite-mut">
                        {row.start_date ?? ""} ~ {row.end_date ?? ""}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-graphite-mut">
                      달성률{" "}
                      <b className={"font-mono " + (pr.ratio != null && pr.ratio >= 1 ? "text-gain" : "text-graphite")}>
                        {pr.ratio != null ? pct(pr.ratio) : "—"}
                      </b>
                      {pr.target != null && (
                        <span className="font-mono text-graphite-mut">
                          {" "}
                          ({usd(pr.sold)}/{usd(pr.target)}대)
                        </span>
                      )}
                      {" · "}누적 매출 <b className="font-mono text-graphite">{krw(pr.revenueKrw)}원</b>
                    </span>
                  </div>
                  <QuotaTrack ct={ct} sold={pr.sold} />
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        {sectionTitle("제품별 요약")}
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={TH}>제품</th>
                {HEAD.map((h) => (
                  <th key={h} className={TH}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byProduct.size === 0 && (
                <tr>
                  <td colSpan={7} className="border border-line px-3 py-4 text-sm text-graphite-mut">
                    집계할 실적이 없습니다.
                  </td>
                </tr>
              )}
              {[...byProduct.entries()]
                .sort((a, b) => b[1].revKrw - a[1].revKrw)
                .map(([name, a]) => (
                  <tr key={name} className="hover:bg-surface-2">
                    <td className="whitespace-nowrap border border-line px-2 py-1 text-sm font-medium text-graphite">{name}</td>
                    {cells(a)}
                  </tr>
                ))}
            </tbody>
            {byProduct.size > 0 && (
              <tfoot>
                <tr className="bg-surface-2">
                  <td className="border border-line-strong px-2 py-1.5 text-sm font-bold text-graphite">전체 합계</td>
                  {cells(total, true)}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      <section>
        {sectionTitle("국가별 요약")}
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={TH}>국가</th>
                {HEAD.map((h) => (
                  <th key={h} className={TH}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byCountry.size === 0 && (
                <tr>
                  <td colSpan={7} className="border border-line px-3 py-4 text-sm text-graphite-mut">
                    집계할 실적이 없습니다. 계약 케이스의 "실적" 단계에서 확정 행을 입력하세요.
                  </td>
                </tr>
              )}
              {[...byCountry.entries()]
                .sort((a, b) => b[1].revKrw - a[1].revKrw)
                .map(([name, a]) => (
                  <tr key={name} className="hover:bg-surface-2">
                    <td className="border border-line px-2 py-1 text-sm font-medium text-graphite">{name}</td>
                    {cells(a)}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-[11px] text-graphite-mut">
        집계 대상 실적 {count}건 · 마진 = 매출(KRW) − (제조원가×수량 + 수수료 + 운임·기타). 관세·현지 세금은 수입자 부담분
        제외 기준.
      </p>
    </div>
  );
}
